import React from 'react';
import { Upload, X, Image as ImageIcon, AlertTriangle } from 'lucide-react';
import { storage } from '../../lib/firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

// Maximum file size
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

// Allowed file types
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

interface ProfileMediaUploaderProps {
  userId: string;
  onUploadComplete: (url: string) => void;
  onUploadError: (error: string) => void;
  existingPhotos?: string[];
  onDeletePhoto?: (url: string) => void;
}

interface UploadProgress {
  progress: number;
  error?: string;
  url?: string;
}

export function ProfileMediaUploader({ 
  userId, 
  onUploadComplete, 
  onUploadError,
  existingPhotos = [],
  onDeletePhoto
}: ProfileMediaUploaderProps) {
  const [isDragging, setIsDragging] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState<UploadProgress | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = async (file: File) => {
    // Reset upload progress
    setUploadProgress(null);

    // Validate file type
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      onUploadError('Type de fichier non supporté. Veuillez utiliser JPG, PNG ou WebP pour les images.');
      return;
    }

    // Validate file size
    if (file.size > MAX_IMAGE_SIZE) {
      onUploadError(`La taille de l'image ne doit pas dépasser ${MAX_IMAGE_SIZE / (1024 * 1024)}MB`);
      return;
    }

    try {
      // Create storage reference
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop() || 'jpg';
      
      // Use the same pattern as the media upload component
      // Make sure userId is included in the storage path
      const basePath = `PVTmates/Photos/${userId}_${timestamp}`;
      const storageRef = ref(storage, `${basePath}.${fileExtension}`);
      
      // Upload file
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      // Listen for upload progress
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress({ progress });
        },
        (error) => {
          console.error('Upload error:', error);
          setUploadProgress({ progress: 0, error: 'Erreur lors de l\'upload' });
          onUploadError('Une erreur est survenue lors de l\'upload');
        },
        async () => {
          // Upload completed successfully
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            setUploadProgress({ progress: 100, url: downloadURL });
            
            // Call the completion callback after a short delay to ensure smooth UI transition
            setTimeout(() => {
              onUploadComplete(downloadURL);
              // Clear progress after completion callback is processed
              setTimeout(() => {
                setUploadProgress(null);
              }, 300);
            }, 500);
            
            // Clear file input
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          } catch (error) {
            console.error('Error getting download URL:', error);
            onUploadError('Erreur lors de la récupération de l\'URL de téléchargement');
            setUploadProgress(null);
          }
        }
      );
    } catch (error) {
      console.error('Error handling file:', error);
      onUploadError('Erreur lors de la gestion du fichier');
      setUploadProgress(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Existing photos display */}
      {existingPhotos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
          {existingPhotos.map((photo, index) => (
            <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 group">
              <img 
                src={photo}
                alt={`Photo ${index + 1}`}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
              {onDeletePhoto && (
                <button
                  onClick={() => onDeletePhoto(photo)}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-red-600"
                  aria-label="Supprimer"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      <div
        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 cursor-pointer ${
          isDragging
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-primary-500 dark:hover:border-primary-400'
        }`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="space-y-4">
          <div className="flex justify-center">
            <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-full">
              <Upload className="w-8 h-8 text-primary-500" />
            </div>
          </div>
          <div>
            <p className="text-base font-medium text-gray-900 dark:text-white">
              Glissez-déposez ou cliquez pour ajouter une photo
            </p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              JPG, PNG ou WebP • Max. 5MB
            </p>
          </div>
          <div className="flex justify-center">
            <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
              <ImageIcon className="w-4 h-4" />
              <span>Images uniquement</span>
            </div>
          </div>
        </div>

        {/* Upload Progress Overlay */}
        {uploadProgress && (
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center rounded-xl">
            {uploadProgress.error ? (
              <div className="text-center text-white p-4 max-w-xs">
                <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-sm">{uploadProgress.error}</p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setUploadProgress(null);
                  }}
                  className="mt-3 px-4 py-2 bg-white text-red-600 rounded-lg text-sm font-medium"
                >
                  Réessayer
                </button>
              </div>
            ) : (
              <div className="text-center text-white">
                <div className="mb-2">
                  <span className="text-lg font-medium">{Math.round(uploadProgress.progress)}%</span>
                </div>
                <div className="w-48 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-500 transition-all duration-300"
                    style={{ width: `${uploadProgress.progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}